import { toast } from "react-toastify";

import { AgenticLogicRuleDto } from "@intelligems/ig-types/dtos/agentic-logic/rules";
import {
  DeleteAgenticLogicRuleRequestPathParamsType,
  DeleteAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/lifecycle-actions/delete";
import {
  PauseAgenticLogicRuleRequestPathParamsType,
  PauseAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/lifecycle-actions/pause";
import {
  ResumeAgenticLogicRuleRequestPathParamsType,
  ResumeAgenticLogicRuleResponseBodyType,
} from "@intelligems/ig-types/functions/agentic-logic/lifecycle-actions/resume";
import { getMutationErrorMessage } from "@src/queryClient";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { api } from "../api";
import { agenticLogicKeys } from "../queryKeys";
import { ApiRequest } from "../types/api";

/**
 * The state-transition verbs are wire-identical — `POST
 * /rules/{ruleId}/{action}` with no body, answering `{ rule }` — and both
 * invalidate the same two keys. They share one typed builder so a change to
 * the transition contract is a single edit; publish (async, its own job) and
 * rollback (versioned, takes a body) are deliberately not part of this set,
 * and delete is the item DELETE below rather than an action.
 */
const buildRuleTransitionHook = <
  TResponse extends { rule: AgenticLogicRuleDto },
  TPathParams extends { ruleId: string },
>(
  action: "pause" | "resume",
  failureLabel: string
) => {
  return () => {
    const queryClient = useQueryClient();

    return useMutation<
      TResponse,
      Error,
      ApiRequest<unknown, TPathParams>,
      unknown
    >({
      mutationFn: async (payload) => {
        const resp = await api.post<TResponse, unknown>(
          `/v2/agentic-logic/rules/${payload.pathParams.ruleId}/${action}`,
          {}
        );
        return resp.data;
      },
      onSuccess: async (_data, payload) => {
        const { ruleId } = payload.pathParams;
        await queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rule(ruleId),
        });
        await queryClient.invalidateQueries({
          queryKey: agenticLogicKeys.rules(),
        });
      },
      onError: (error) => {
        const errorDetail = getMutationErrorMessage(error);
        toast.error(
          `Failed to ${failureLabel}${errorDetail ? `. ${errorDetail}` : ""}`
        );
      },
    });
  };
};

export const usePauseAgenticLogicRule = buildRuleTransitionHook<
  PauseAgenticLogicRuleResponseBodyType,
  PauseAgenticLogicRuleRequestPathParamsType
>("pause", "pause rule");

export const useResumeAgenticLogicRule = buildRuleTransitionHook<
  ResumeAgenticLogicRuleResponseBodyType,
  ResumeAgenticLogicRuleRequestPathParamsType
>("resume", "resume rule");

const deleteAgenticLogicRule = async (
  payload: ApiRequest<unknown, DeleteAgenticLogicRuleRequestPathParamsType>
) => {
  const resp = await api.delete<DeleteAgenticLogicRuleResponseBodyType>(
    `/v2/agentic-logic/rules/${payload.pathParams.ruleId}`
  );
  return resp.data;
};

/**
 * Hard delete is legal from every state — it is how a rule is retired — and
 * rejects while a live experiment arm or placement still references it, naming
 * what to detach.
 */
export const useDeleteAgenticLogicRule = () => {
  const queryClient = useQueryClient();

  return useMutation<
    DeleteAgenticLogicRuleResponseBodyType,
    Error,
    ApiRequest<unknown, DeleteAgenticLogicRuleRequestPathParamsType>,
    unknown
  >({
    mutationFn: async (data) => deleteAgenticLogicRule(data),
    onSuccess: async (_data, payload) => {
      // The rule is gone: drop its cache entry rather than invalidating it,
      // which would refetch a 404.
      queryClient.removeQueries({
        queryKey: agenticLogicKeys.rule(payload.pathParams.ruleId),
      });
      await queryClient.invalidateQueries({
        queryKey: agenticLogicKeys.rules(),
      });
    },
    onError: (error) => {
      const errorDetail = getMutationErrorMessage(error);
      toast.error(
        `Failed to delete rule${errorDetail ? `. ${errorDetail}` : ""}`
      );
    },
  });
};
